// Generic type-ahead component — same UX as the pest-control autocomplete
// but caller supplies (a) the match function, (b) how to render each option,
// and (c) what to do when an option is picked. Keeps every vertical's
// autocomplete consistent so the UI feels the same everywhere.
import { useEffect, useRef, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";

export default function CatalogAutocomplete({
  value,
  onChange,
  onSelect,           // (item) => void
  matchFn,            // (q, limit) => items
  renderOption,       // (item, i, isActive) => JSX
  keyFn = (it) => it.name || it.model,
  placeholder,
  className,
  testid,
  disclaimer,
}) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);
  const suggestions = matchFn(value, 8);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (item) => { onChange(keyFn(item)); onSelect?.(item); setOpen(false); };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && suggestions[hi]) { e.preventDefault(); pick(suggestions[hi]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={`${testid}-wrap`}>
      <div className="relative">
        <MagnifyingGlass weight="bold" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
        <input
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          data-testid={testid}
          autoComplete="off"
          className={className ? `${className} pl-9` : "w-full h-11 pl-9 pr-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"}
        />
      </div>
      {open && suggestions.length > 0 && (
        <div data-testid={`${testid}-dropdown`}
          className="absolute z-30 left-0 right-0 top-full mt-1 max-h-72 overflow-y-auto bg-white border-2 border-slate-200 rounded-lg shadow-xl">
          {suggestions.map((item, i) => (
            <button key={keyFn(item)} type="button"
              onMouseEnter={() => setHi(i)}
              onClick={() => pick(item)}
              data-testid={`${testid}-opt-${i}`}
              className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 ${i === hi ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
              {renderOption(item, i, i === hi)}
            </button>
          ))}
          {disclaimer && (
            <div className="px-3 py-2 text-[10px] text-slate-400 border-t border-slate-100 font-medium">{disclaimer}</div>
          )}
        </div>
      )}
    </div>
  );
}
